// Novidades do jogo (página /novidades). A versão mais nova fica no topo.
// Para lançar uma versão: adicione uma entrada com os 4 idiomas.
import type { Lang } from "./i18n";

export interface ChangelogEntry {
  version: string;
  date: string; // AAAA-MM-DD
  items: Record<Lang, string[]>;
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: "0.7.0",
    date: "2026-10-10",
    items: {
      "pt-BR": [
        "Tutorial guiado com o Professor Carvalho: aprenda a jogar em 3 minutos e ganhe ₽ 300 na primeira vez.",
        "Conquistas: 34 desafios com Pokédollar, títulos para o perfil e molduras para a foto.",
        "Denunciar e bloquear: botão ⚑ ao lado do nome no chat, na sala e no fim da partida.",
        "Compartilhar resultado: gere um card com sua colocação e seu time para postar.",
        "Salas com menos de 8 podem ser completadas com treinadores do jogo (botão no lobby). Na fila pública isso acontece sozinho depois de 1:30.",
      ],
      en: [
        "Guided tutorial with Professor Oak: learn in 3 minutes and earn ₽ 300 the first time.",
        "Achievements: 34 challenges with Pokédollars, profile titles and avatar frames.",
        "Report and block: ⚑ button next to names in chat, in the lobby and at the end of a match.",
        "Share your result: create a card with your placement and team to post.",
        "Rooms with fewer than 8 players can be filled with game-controlled trainers (lobby button). In the public queue this happens automatically after 1:30.",
      ],
      es: [
        "Tutorial guiado con el Profesor Oak: aprende en 3 minutos y gana ₽ 300 la primera vez.",
        "Logros: 34 desafíos con Pokédollar, títulos de perfil y marcos para la foto.",
        "Denunciar y bloquear: botón ⚑ junto al nombre en el chat, en la sala y al final de la partida.",
        "Comparte tu resultado: crea una tarjeta con tu puesto y tu equipo para publicar.",
        "Las salas con menos de 8 pueden completarse con entrenadores del juego (botón en la sala). En la cola pública ocurre solo tras 1:30.",
      ],
      ja: [
        "オーキド博士のガイド付きチュートリアル:3分で遊び方を覚え、初回は₽300獲得。",
        "実績:34の挑戦でポケドル、プロフィールの称号、アイコンの枠がもらえます。",
        "通報とブロック:チャット、ロビー、試合終了画面の名前の横にある⚑ボタン。",
        "結果をシェア:順位とチームのカードを作って投稿できます。",
        "8人未満の部屋はゲームのトレーナーで埋められます(ロビーのボタン)。公開キューでは1分30秒後に自動で埋まります。",
      ],
    },
  },
  {
    version: "0.6.0",
    date: "2026-10-10",
    items: {
      "pt-BR": [
        "Fila por nível de habilidade: \"Jogar\" e \"Ranqueada\" procuram adversários parecidos com você, abrindo a faixa a cada 15 s.",
        "Jogando sozinho, você enfrenta tabuleiros reais de outros treinadores na mesma rodada.",
        "Atualizações do servidor não derrubam mais a partida: você é levado de volta sozinho.",
        "Dá para voltar a uma partida em andamento pelo menu, mesmo depois de fechar o app.",
        "Efeitos sonoros e vibração (botão 🔊 no topo).",
        "Balanceamento das cartas: Energias 2/3/4% (Voador 1/2/3%), Poké Radar 2/3/4%, Compartilhar EXP 1/2/3, Poção 3/5/6, Repelente 30%, Mochila de cobre na 3-1. Fogo e Gelo um pouco mais fortes.",
      ],
      en: [
        "Skill-based queue: \"Play\" and \"Ranked\" look for opponents close to your level, widening every 15 s.",
        "Playing solo, you face real boards from other trainers at the same round.",
        "Server updates no longer drop your match: you're taken back automatically.",
        "You can rejoin a match in progress from the menu, even after closing the app.",
        "Sound effects and vibration (🔊 button at the top).",
        "Card balance: Energies 2/3/4% (Flying 1/2/3%), Poké Radar 2/3/4%, Exp. Share 1/2/3, Potion 3/5/6, Repel 30%, copper Bag on 3-1. Fire and Ice slightly stronger.",
      ],
      es: [
        "Cola por nivel de habilidad: \"Jugar\" y \"Clasificatoria\" buscan rivales parecidos a ti, ampliando el rango cada 15 s.",
        "Jugando solo, te enfrentas a tableros reales de otros entrenadores en la misma ronda.",
        "Las actualizaciones del servidor ya no cortan la partida: vuelves automáticamente.",
        "Puedes volver a una partida en curso desde el menú, incluso tras cerrar la app.",
        "Efectos de sonido y vibración (botón 🔊 arriba).",
        "Balance de cartas: Energías 2/3/4% (Volador 1/2/3%), Poké Radar 2/3/4%, Repartir Exp 1/2/3, Poción 3/5/6, Repelente 30%, Mochila de cobre en la 3-1. Fuego y Hielo algo más fuertes.",
      ],
      ja: [
        "実力マッチング：「プレイ」と「ランク」は近い実力の相手を探し、15秒ごとに範囲が広がります。",
        "ひとりでプレイすると、同じラウンドの他のトレーナーの本物の盤面と戦います。",
        "サーバー更新で試合が途切れなくなりました：自動で戻ります。",
        "アプリを閉じた後でも、メニューから進行中の試合に戻れます。",
        "効果音と振動（上部の🔊ボタン）。",
        "カード調整：エネルギー2/3/4%（ひこう1/2/3%）、ポケトレ2/3/4%、がくしゅうそうち1/2/3、キズぐすり3/5/6、むしよけ30%、ブロンズのバッグは3-1。ほのおとこおりを少し強化。",
      ],
    },
  },
  {
    version: "0.5.0",
    date: "2026-10-09",
    items: {
      "pt-BR": [
        "Missões diárias: 4 por dia (sinergias, vitórias, ranqueadas e mais) com prêmio em Pokédollar.", "Rankings novos: nível, missões concluídas e dias seguidos jogando.", "O jogo agora se chama PokéChess!",
        "Os 151 Pokémon de Kanto! 22 linhas novas são liberadas no Criadouro: crie o Pokémon até o nível 5 e ele passa a aparecer na sua loja.",
        "Pokédollar: ganhe de 0 a 100 por partida (conta nova começa com 1000).",
        "Cartas começam bloqueadas e são compradas com Pokédollar no Deck.",
        "Shinies à venda na Coleção.",
        "Mercado: compre itens para o seu item pool, incluindo 10 itens especiais que caem prontos na partida.",
        "Foto de perfil: escolha um Pokémon no menu.",
        "A partida começa com 5 de ouro em vez de uma unidade.",
        "Ranqueada liberada sem confirmação de e-mail.",
      ],
      en: [
        "Daily missions: 4 per day (synergies, wins, ranked and more) with Pokédollar rewards.", "New rankings: level, missions completed and login streak.", "The game is now called PokéChess!",
        "All 151 Kanto Pokémon! 22 new lines are unlocked in the Daycare: raise the Pokémon to level 5 and it starts showing up in your shop.",
        "Pokédollars: earn 0 to 100 per match (new accounts start with 1000).",
        "Cards start locked and are bought with Pokédollars in the Deck.",
        "Shinies for sale in the Collection.",
        "Poké Mart: buy items for your item pool, including 10 special items that drop ready-made in matches.",
        "Profile picture: pick a Pokémon in the menu.",
        "Matches start with 5 gold instead of a unit.",
        "Ranked no longer requires email confirmation.",
      ],
      es: [
        "Misiones diarias: 4 por día (sinergias, victorias, clasificatorias y más) con premios en Pokédólares.", "Nuevos rankings: nivel, misiones completadas y días seguidos jugando.", "¡El juego ahora se llama PokéChess!",
        "¡Los 151 Pokémon de Kanto! 22 líneas nuevas se desbloquean en la Guardería: cría al Pokémon hasta el nivel 5 y empezará a salir en tu tienda.",
        "Pokédólares: gana de 0 a 100 por partida (las cuentas nuevas empiezan con 1000).",
        "Las cartas empiezan bloqueadas y se compran con Pokédólares en el Mazo.",
        "Variocolores a la venta en la Colección.",
        "Tienda: compra objetos para tu reserva, incluidos 10 objetos especiales que caen ya montados en la partida.",
        "Foto de perfil: elige un Pokémon en el menú.",
        "La partida empieza con 5 de oro en lugar de una unidad.",
        "La clasificatoria ya no exige confirmar el correo.",
      ],
      ja: [
        "デイリーミッション：1日4つ（シナジー、勝利、ランクなど）、報酬はポケドル。", "新しいランキング：レベル、ミッション達成数、連続ログイン日数。", "ゲーム名がPokéChessになりました！",
        "カントーの151匹がそろった！新しい22系統は育て屋で解放：レベル5まで育てるとショップに並びます。",
        "ポケドル: 1試合で0〜100もらえます（新規アカウントは1000から）。",
        "カードは最初はロックされていて、デッキ画面でポケドルで買います。",
        "コレクションで色違いを販売。",
        "ショップ: アイテムプール用のアイテムを購入。試合で完成品として出るスペシャルアイテム10種も。",
        "プロフィール画像: メニューでポケモンを選べます。",
        "試合はユニットの代わりにゴールド5で始まります。",
        "ランクマッチにメール確認が不要になりました。",
      ],
    },
  },
  {
    version: "0.4.0",
    date: "2026-10-09",
    items: {
      "pt-BR": [
        "Pokémon animados (Mystery Dungeon) andando, atacando e reagindo no combate.",
        "Ícones e explicação (tooltip) dos itens e das sinergias.",
        "Loja VIP com tabuleiros-arena, 4 deles animados.",
        "Jogo em português, inglês, espanhol e japonês.",
      ],
      en: [
        "Animated Pokémon (Mystery Dungeon) walking, attacking and reacting in combat.",
        "Icons and tooltips for items and synergies.",
        "VIP Shop with arena boards, 4 of them animated.",
        "Game in Portuguese, English, Spanish and Japanese.",
      ],
      es: [
        "Pokémon animados (Mystery Dungeon) que caminan, atacan y reaccionan en el combate.",
        "Iconos y descripciones emergentes de objetos y sinergias.",
        "Tienda VIP con tableros-arena, 4 de ellos animados.",
        "Juego en portugués, inglés, español y japonés.",
      ],
      ja: [
        "ポケモンがアニメーション（不思議のダンジョン）で歩き、攻撃し、反応します。",
        "アイテムとシナジーのアイコンと説明（ツールチップ）。",
        "アリーナボードのVIPショップ（4つはアニメーション付き）。",
        "ポルトガル語・英語・スペイン語・日本語に対応。",
      ],
    },
  },
  {
    version: "0.3.0",
    date: "2026-10-09",
    items: {
      "pt-BR": ["Partidas de 1 a 8 pessoas, sala com código para jogar com amigos.", "Deck de cartas, ranking por season e coleção de shinies."],
      en: ["Matches for 1 to 8 people, room codes to play with friends.", "Card deck, seasonal ranking and shiny collection."],
      es: ["Partidas de 1 a 8 personas, sala con código para jugar con amigos.", "Mazo de cartas, clasificación por temporada y colección de variocolores."],
      ja: ["1〜8人の対戦、部屋コードで友だちと遊べます。", "カードデッキ、シーズンランキング、色違いコレクション。"],
    },
  },
];

const SEEN_KEY = "pokechess:changelog-seen";

/** Há novidades que o jogador ainda não viu? */
export function hasUnseenChangelog(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) !== CHANGELOG[0]!.version;
  } catch {
    return false;
  }
}

export function markChangelogSeen() {
  try {
    localStorage.setItem(SEEN_KEY, CHANGELOG[0]!.version);
  } catch {
    /* ok */
  }
}
